// @ts-strict-ignore
import moment from 'moment';
import styled from 'styled-components';
import tw from 'twin.macro';

import { CheckBox } from '@/atoms/Check';
import { Icon } from '@/atoms/Icon';
import { IconContainer } from '@/atoms/IconContainer/IconContainer';
import type { SearchNavigatorType } from '@/atoms/SearchNavigator/SearchNavigator';
import { SearchNavigator as SearchNavigatorImpl } from '@/atoms/SearchNavigator/SearchNavigator';
import type { TagElementType } from '@/atoms/Tags/Tags';
import { Tooltip } from '@/atoms/Tooltip/Tooltip';
import { useIsFromNotebookSection } from '@/components/SourceCards/hooks/useIsFromNotebookSection';
import { Spacer } from '@/components/Spacer';
import { Tags } from '@/components/UserTags';
import { NEEDL_BOOKMARK_TAG } from '@/components/UserTags/common';
import { useDocumentTags } from '@/components/UserTags/hooks/useDocumentTags';
import { ContactInfo } from '@/features/ManageContacts/ContactInfo';
import { useResponsiveConstants } from '@/hooks/responsive/useResponsiveConstants';
import BookmarkIcon from '@/icons/bookmark_fill.svg?react';
import TagIcon from '@/icons/tag-active.svg?react';
import type { FeedItemType, HandlersType } from '@/types';
import { bytesToSize } from '@/utils/bytesToSize';
import { getNormalizedName } from '@/utils/getNormalizedName';
import { getSubReddit } from '@/utils/getSubReddit';

type CardSelectType = {
  /**
   * Banner image (front face)
   */
  BannerImage?: React.ReactNode;
  /**
   * should the checkbox be disabled
   */
  disabled?: boolean;
  /**
   * index of the card
   */
  index: number;
  /**
   * handlers
   */
  handlers: HandlersType;
  /**
   * is the card selected
   */
  selected: boolean;
};

type CardMetaHeaderType = {
  result: FeedItemType;
};

type CardMetaHeaderImplType = {
  deeplink: string;
};

type CardTitleType = {
  /**
   * Banner image (front face)
   */
  icon: React.ReactNode;
  /**
   * leading text for title
   */
  leading?: React.ReactNode;
  /**
   * attachment
   */
  title?: React.ReactNode;
};

type GetSocialTitleReturnType = {
  title: React.ReactNode;
  secondaryTitle?: React.ReactNode;
};

const TitleContainer = styled.div`
  ${tw`flex space-x-2 items-center`}

  & > *:is(img, svg) {
    ${tw`w-6 h-6 flex-shrink-0 object-contain`}
  }
`;

const CardSelection = ({
  index,
  handlers,
  selected,
  disabled,
}: CardSelectType) => {
  const { toggleHandler } = handlers;

  if (disabled) {
    return null;
  }

  return (
    <CheckBox
      checked={selected}
      size='large'
      padding
      onChange={() => toggleHandler(index)}
    />
  );
};

const DownloadTooltip: React.FunctionComponent = ({ children }) => {
  return <Tooltip label='Download'>{children}</Tooltip>;
};

type ReturnType = TagElementType[];

export const commonDocumentTags = (result: FeedItemType): ReturnType => {
  return [
    { tagKey: 'Title', value: result.title },
    { tagKey: 'Author institution', value: result.author_institution },
    { tagKey: 'Report type', value: result.report_type },
    { tagKey: 'Company name', value: result.company_name },
    { tagKey: 'Listed', value: result.listed },
    { tagKey: 'ISIN', value: result.isin },
    { tagKey: 'CIN', value: result.cin },
    { tagKey: 'Quarter', value: result.quarter },
    { tagKey: 'Company covered', value: result.main_company },
    { tagKey: 'ISBN', value: result.isbn },
    {
      tagKey: 'Company coverage type',
      value: result.company_coverage_type,
    },
    {
      tagKey: 'Pages',
      value: result.num_pages?.toString(),
    },
    { tagKey: 'Report year', value: result.year },
    {
      tagKey: 'Report date',
      value: moment.unix(result.unix).format('DD-MM-YYYY'),
    },
    { tagKey: 'Industry', value: result.industry },
    { tagKey: 'Instrument', value: result.instrument },
    { tagKey: 'Book authors', value: result.book_authors?.join(', ') },
    { tagKey: 'Recommendation', value: result.recommendation },
    { tagKey: 'Price', value: result.price_target },
    {
      tagKey: 'Sector coverage type',
      value: result.sector_coverage_type,
    },
    {
      tagKey: 'Created by',
      value: result.user_name,
    },
    {
      tagKey: 'Size',
      value: result.file_size ? bytesToSize(result.file_size) : undefined,
    },
  ];
};

const ShareTooltip: React.FunctionComponent = ({ children }) => {
  return <Tooltip label='Share'>{children}</Tooltip>;
};

const EditTooltip: React.FunctionComponent = ({ children }) => {
  return <Tooltip label='Edit'>{children}</Tooltip>;
};

const DeleteTooltip: React.FunctionComponent = ({ children }) => {
  return <Tooltip label='Delete'>{children}</Tooltip>;
};

const PrintTooltip: React.FunctionComponent = ({ children }) => {
  return <Tooltip label='Print'>{children}</Tooltip>;
};

const QuickRemovalTooltip: React.FunctionComponent = ({ children }) => {
  return <Tooltip label='Hide'>{children}</Tooltip>;
};

const CustomLabelTooltip: React.FunctionComponent<{
  label?: React.ReactNode | string;
}> = ({ children, label }) => {
  return <Tooltip label={label}>{children}</Tooltip>;
};

const StyledIconContainer = styled(IconContainer)`
  ${tw`opacity-50`}
`;

const CardMetaHeaderImpl = ({ deeplink }: CardMetaHeaderImplType) => {
  const { data: documentTagsData, isLoading: documentTagsLoading } =
    useDocumentTags(deeplink);

  const isBookmarked = documentTagsData?.tags?.includes(NEEDL_BOOKMARK_TAG);
  const documentTags = documentTagsData?.userTags;
  const showTags =
    !documentTagsLoading && documentTags && documentTags.length > 0;

  if (!isBookmarked && !showTags) {
    return null;
  }

  return (
    <div className='flex space-x-4 justify-end sm:items-center'>
      {isBookmarked ? (
        <Icon size='small'>
          <StyledIconContainer>
            <BookmarkIcon aria-label='has bookmark' />
          </StyledIconContainer>
        </Icon>
      ) : null}
      {showTags ? (
        <Icon size='small'>
          <StyledIconContainer>
            <TagIcon aria-label='has tags' />
          </StyledIconContainer>
        </Icon>
      ) : null}
    </div>
  );
};

const CardMetaHeader = ({ result }: CardMetaHeaderType) => {
  const isNotebooksSection = useIsFromNotebookSection(result);

  if (!result?.deeplink || isNotebooksSection) {
    return null;
  }

  return <CardMetaHeaderImpl deeplink={result?.deeplink} />;
};

const CardTitleContainer = ({ icon, leading, title }: CardTitleType) => {
  return (
    <TitleContainer>
      {leading}
      <Icon className='flex-shrink-0'>{icon}</Icon>
      {title}
    </TitleContainer>
  );
};

const getSocialTitle = (result: FeedItemType): GetSocialTitleReturnType => {
  switch (result.source) {
    case 'twitter':
      return {
        title: getNormalizedName(result.name),
        secondaryTitle: (
          <ContactInfo
            source={result.source}
            contacts={result.contacts_for_people_filter}
          >
            @{result.user}
          </ContactInfo>
        ),
      };
    case 'reddit':
      return {
        title: getSubReddit(
          Array.isArray(result.link) ? result.link[0] : result.link ?? ''
        ),
      };
    default:
      return { title: '' };
  }
};

const DocTags = ({
  deeplink,
  source,
}: {
  deeplink: string;
  source: string;
}) => {
  const { data: documentTags, isLoading: documentTagsLoading } =
    useDocumentTags(deeplink);

  return (
    <>
      {!documentTagsLoading &&
      (!!documentTags?.userTags || documentTags?.sharedTags) ? (
        <>
          <Spacer axis='vertical' size='1rem' />
          <Tags
            tags={[
              ...(documentTags.sharedTags?.length > 0
                ? documentTags.sharedTags.map<TagElementType>((tag) => ({
                    value: tag,
                    color: 'warn',
                    type: 'shared_tags',
                    source,
                  }))
                : [{ value: '' }]),
              ...(documentTags.userTags?.length > 0
                ? documentTags.userTags.map<TagElementType>((tag) => ({
                    value: tag,
                    color: 'info',
                    type: 'user_tags',
                    source,
                  }))
                : [{ value: '' }]),
            ]}
          />
        </>
      ) : null}
    </>
  );
};

const SearchNavigator = ({
  currentPage,
  onBack,
  onForward,
  totalHits,
}: SearchNavigatorType) => {
  const { mobile } = useResponsiveConstants();

  const showClipControls = !!totalHits;

  if (mobile || !showClipControls) {
    return null;
  }

  return (
    <div className='flex items-center'>
      <SearchNavigatorImpl
        currentPage={currentPage}
        onBack={onBack}
        onForward={onForward}
        totalHits={totalHits}
      />
      <VeriticalDivide />
    </div>
  );
};

const hasResultSourceFile = (result: FeedItemType) => {
  return (
    result.source === 'needldrive' &&
    result.upload_client &&
    ['clipped_report', 'table_extraction'].includes(result.upload_client)
  );
};

const VeriticalDivide = styled.div`
  ${tw`w-px h-5 my-1.5 bg-gray-300`}
`;

export {
  DownloadTooltip,
  ShareTooltip,
  EditTooltip,
  DeleteTooltip,
  PrintTooltip,
  QuickRemovalTooltip,
  CustomLabelTooltip,
  CardMetaHeader,
  CardTitleContainer,
  CardSelection,
  getSocialTitle,
  DocTags,
  VeriticalDivide,
  SearchNavigator,
  hasResultSourceFile,
};
